import { appInfoSelector, locationSelector } from '@common/redux/selectors/app';
import { profileSelector } from '@common/redux/selectors/auth';
import {
  currentRecordSelector,
  realtimeDatabaseSelector,
} from '@common/redux/selectors/database';
import { getValueFields } from '@common/redux/selectors/formInputs';
import { localeSelector } from '@common/redux/selectors/language';
import {
  eachRecursiveTable,
  flattenInputObject,
  memoriesConditional,
} from '@common/utils/component';
import { get } from 'lodash';
import React, { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';

const RenderConnector = (Render: FC<any>) => {
  return (props: any) => {
    const { screen } = props;
    const valueInputs = useSelector(getValueFields);
    const appInfor = useSelector(appInfoSelector);
    const currentRecord = useSelector(currentRecordSelector);
    const databases = useSelector(realtimeDatabaseSelector);
    const loggedUser = useSelector(profileSelector);
    const currentLocation = useSelector(locationSelector);
    const locale = useSelector(localeSelector);

    const tableRefresh = useMemo(() => {
      const pullToRefresh = get(screen, 'attributes.pullToRefresh', false);
      if (!pullToRefresh) return [];

      const tables = eachRecursiveTable(screen);
      return tables.reduce((preValue: any, current: string) => {
        return { ...preValue, [current]: databases[current] };
      }, {});
    }, [databases, screen]);

    const dependencies: any = useMemo(() => {
      const currentInputValues = JSON.parse(JSON.stringify(valueInputs));
      const inputDependencies = flattenInputObject(screen);

      const values = inputDependencies.reduce(
        (preValue: any, current: string) => {
          return { ...preValue, [current]: currentInputValues[current] };
        },
        {}
      );

      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      return {
        appInfor,
        valueInputs: values,
        routeParam: currentRecord,
        table: tableRefresh,
        timeZone,
        loggedUser: loggedUser?.id,
        currentLocation,
        locale,
      };
    }, [
      valueInputs,
      appInfor,
      currentRecord,
      screen?.screenId,
      tableRefresh,
      loggedUser,
      currentLocation,
    ]);

    return (
      <MemoryComponent Render={Render} {...props} dependencies={dependencies} />
    );
  };
};

const MemoryComponent = React.memo((props: any) => {
  const { Render, ...property } = props;
  return <Render {...property} />;
}, memoriesConditional(['dependencies', 'screen', 'Render']));

export default RenderConnector;
